/**
 * "Assign courses" side pane, shared by Users / My Students / Batch detail.
 *
 *   const assign = useAssignCoursesPane({ api, onDone: fetchData });
 *   … selectionActions: { key:'assign', label:'Assign courses', icon: BookOpen, requires:'any', onClick: (rows) => assign.open(rows) }
 *   … <SplitLayout side={side || assign.side}>
 *
 * Shows a searchable checkbox list of the tenant's courses; for a single user the courses they are
 * already enrolled in are pre-ticked and locked. Submits POST /enrollments/assign.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, MagnifyingGlass, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { paneForm } from '../shell/SidePane';
import { Checkbox } from '../ui/checkbox';
import { StatusBadge } from '../datagrid/DataGrid';

export function CourseChecklist({ courses, selected, onToggle, locked = new Set(), query, onQuery, testid = 'course-checklist' }) {
  const q = (query || '').trim().toLowerCase();
  const shown = courses.filter((c) => !q || c.title.toLowerCase().includes(q) || (c.course_code || '').toLowerCase().includes(q));
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="relative">
        <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Search courses" className="w-full h-8 pl-8 pr-2 rounded border border-input text-[13px] outline-none focus:border-shell-primary" />
      </div>
      <div className="max-h-[50vh] overflow-y-auto rounded border border-border divide-y">
        {shown.length === 0 && <p className="p-3 text-xs text-muted-foreground">No courses match.</p>}
        {shown.map((c) => {
          const isLocked = locked.has(c.id);
          const checked = isLocked || selected.has(c.id);
          return (
            <label key={c.id} className={`flex items-center gap-2.5 px-2.5 py-1.5 text-[13px] ${isLocked ? 'bg-muted/40 text-muted-foreground' : 'hover:bg-muted/30 cursor-pointer'}`}>
              <Checkbox checked={checked} disabled={isLocked} onCheckedChange={() => onToggle(c.id)} data-testid={`course-check-${c.id}`} />
              <span className="flex-1 min-w-0 truncate">{c.title}{c.course_code ? <span className="text-muted-foreground"> · {c.course_code}</span> : null}</span>
              {isLocked ? <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" weight="fill" /> enrolled</span> : !c.is_published && <StatusBadge value="draft" />}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function useAssignCoursesPane({ api, onDone }) {
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [locked, setLocked] = useState(new Set());
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!users.length) return;
    api.get('/courses').then((r) => setCourses(r.data || [])).catch(() => setCourses([]));
    if (users.length === 1) {
      api.get(`/users/${users[0].id}/enrollments`).then((r) => setLocked(new Set((r.data || []).map((e) => e.course_id)))).catch(() => setLocked(new Set()));
    } else {
      setLocked(new Set());
    }
  }, [api, users]);

  const open = (rows) => { setUsers(rows); setSelected(new Set()); setQuery(''); };
  const close = () => { setUsers([]); setSelected(new Set()); };
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const submit = async (e) => {
    e?.preventDefault?.();
    if (selected.size === 0) { toast.error('Pick at least one course'); return; }
    setSaving(true);
    try {
      const res = await api.post('/enrollments/assign', { user_ids: users.map((u) => u.id), course_ids: [...selected] });
      toast.success(`${res.data.enrolments_created} enrolment${res.data.enrolments_created === 1 ? '' : 's'} created${res.data.already_enrolled ? ` · ${res.data.already_enrolled} already enrolled` : ''}`);
      onDone?.(res.data);
      close();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign courses');
    } finally {
      setSaving(false);
    }
  };

  const side = useMemo(() => {
    if (!users.length) return null;
    const names = users.slice(0, 3).map((u) => `${u.first_name} ${u.last_name}`).join(', ') + (users.length > 3 ? ` +${users.length - 3} more` : '');
    const f = paneForm({
      id: 'assign-courses-form', onSubmit: submit, onCancel: close, submitLabel: `Assign ${selected.size || ''} course${selected.size === 1 ? '' : 's'}`.replace('  ', ' '), submitting: saving, submitIcon: BookOpen, submitDisabled: selected.size === 0,
      children: (
        <>
          <div className="rounded border border-border bg-muted/30 px-3 py-2 text-[13px]">
            <span className="text-muted-foreground">For: </span>{names}
          </div>
          <CourseChecklist courses={courses} selected={selected} onToggle={toggle} locked={locked} query={query} onQuery={setQuery} />
          <p className="field-hint">Each selected course is added to every selected user. Users already enrolled are skipped. Draft courses can be assigned now and become visible once published.</p>
        </>
      ),
    });
    return { open: true, onOpenChange: close, title: 'Assign courses', subtitle: `${users.length} user${users.length === 1 ? '' : 's'}`, icon: BookOpen, children: f.body, footer: f.footer, testid: 'assign-courses-pane', storageKey: 'pane-assign-courses' };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, courses, selected, locked, query, saving]);

  return { open, close, side, isOpen: users.length > 0 };
}
